import type { UserType } from "@/types/account";
import { containerService } from "@/data/mock-containers";
import { accountService } from "@/data/mock-accounts";
import { addressBookService } from "@/data/mock-address-book";
import { documentService } from "@/data/mock-documents";
import type { EntityType } from "./command-menu-context";

export interface SearchResult {
    id: string;
    type: EntityType;
    title: string;
    subtitle?: string;
    url: string;
    icon?: string;
    metadata?: Record<string, any>;
}

export interface DocumentGroup {
    containerId: string;
    containerNumber: string;
    containerUrl: string;
    documents: SearchResult[];
}

// Utility per determinare userType corrente
export const getCurrentUserType = (): UserType | null => {
    const userType = localStorage.getItem("userType");
    if (userType === "SHIPPER" || userType === "RECEIVER" || userType === "ADMIN" || userType === "FREIGHT_FORWARDER") {
        return userType;
    }
    // Fallback: determinare dal path
    if (typeof window !== "undefined") {
        const path = window.location.pathname;
        if (path.startsWith("/shipper")) return "SHIPPER";
        if (path.startsWith("/receiver")) return "RECEIVER";
        if (path.startsWith("/admin")) return "ADMIN";
        if (path.startsWith("/freight-forwarder")) return "FREIGHT_FORWARDER";
    }
    return null;
};

// Utility per determinare il base path per la navigazione
export const getBasePath = (): string => {
    const userType = getCurrentUserType();
    if (userType === "SHIPPER") return "/shipper";
    if (userType === "RECEIVER") return "/receiver";
    if (userType === "ADMIN") return "/admin";
    if (userType === "FREIGHT_FORWARDER") return "/freight-forwarder";
    return "";
};

// Mappatura shortcut -> entity
export const parseShortcut = (query: string): { searchTerm: string; entity: EntityType } => {
    const trimmed = query.trim();
    
    // Shortcut con >
    if (trimmed.startsWith(">")) {
        const shortcut = trimmed.split(/\s/)[0].toLowerCase();
        const searchTerm = trimmed.substring(shortcut.length).trim();
        
        if (shortcut === ">c" || shortcut === ">container") {
            return { searchTerm, entity: "container" };
        }
        if (shortcut === ">s" || shortcut === ">shipper") {
            return { searchTerm, entity: "shipper" };
        }
        if (shortcut === ">r" || shortcut === ">receiver") {
            return { searchTerm, entity: "receiver" };
        }
        if (shortcut === ">a" || shortcut === ">account") {
            return { searchTerm, entity: "account" };
        }
        if (shortcut === ">ad" || shortcut === ">address") {
            return { searchTerm, entity: "address" };
        }
        if (shortcut === ">d" || shortcut === ">doc" || shortcut === ">document") {
            return { searchTerm, entity: "document" };
        }
    }
    
    return { searchTerm: trimmed, entity: null };
};

// Verifica permessi per entità
export const canSearchEntity = (entity: EntityType, userType: UserType | null): boolean => {
    if (!entity || !userType) return false;
    
    // Gli admin possono cercare solo account
    if (userType === "ADMIN") {
        return entity === "account";
    }
    
    switch (entity) {
        case "container":
            return userType === "SHIPPER" || userType === "RECEIVER";
        case "shipper":
            return userType === "RECEIVER";
        case "receiver":
            return userType === "SHIPPER";
        case "account":
            return false; // Solo admin possono cercare account (gestito sopra)
        case "address":
            return userType === "SHIPPER" || userType === "RECEIVER";
        case "document":
            return userType === "SHIPPER" || userType === "RECEIVER";
        default:
            return false;
    }
};

// Interfaccia per gli shortcut disponibili
export interface AvailableShortcut {
    shortcut: string;
    entity: EntityType;
    description: string;
}

// Ottieni gli shortcut disponibili per il tipo di utente corrente
export const getAvailableShortcuts = (userType: UserType | null): AvailableShortcut[] => {
    const shortcuts: AvailableShortcut[] = [];
    
    if (!userType) return shortcuts;
    
    // Shortcut per admin
    if (userType === "ADMIN") {
        shortcuts.push({
            shortcut: ">a",
            entity: "account",
            description: "Search accounts"
        });
        return shortcuts;
    }
    
    // Shortcut per shipper
    if (userType === "SHIPPER") {
        shortcuts.push(
            {
                shortcut: ">c",
                entity: "container",
                description: "Search containers"
            },
            {
                shortcut: ">r",
                entity: "receiver",
                description: "Search receivers"
            },
            {
                shortcut: ">ad",
                entity: "address",
                description: "Search address book"
            },
            {
                shortcut: ">d",
                entity: "document",
                description: "Search documents"
            }
        );
        return shortcuts;
    }
    
    // Shortcut per receiver
    if (userType === "RECEIVER") {
        shortcuts.push(
            {
                shortcut: ">c",
                entity: "container",
                description: "Search containers"
            },
            {
                shortcut: ">s",
                entity: "shipper",
                description: "Search shippers"
            },
            {
                shortcut: ">ad",
                entity: "address",
                description: "Search address book"
            },
            {
                shortcut: ">d",
                entity: "document",
                description: "Search documents"
            }
        );
        return shortcuts;
    }
    
    return shortcuts;
};

// Ricerca containers
const searchContainers = (searchTerm: string, userType: UserType | null, basePath: string): SearchResult[] => {
    if (!searchTerm) return [];
    
    const containers = containerService.search({ search: searchTerm });
    
    // Filtra per userType se necessario
    let filtered = containers;
    if (userType === "SHIPPER") {
        const accountId = localStorage.getItem("accountId");
        if (accountId) {
            filtered = containers.filter((c) => c.shipperId === accountId);
        }
    } else if (userType === "RECEIVER") {
        const accountId = localStorage.getItem("accountId");
        if (accountId) {
            filtered = containers.filter((c) => c.receiverId === accountId);
        }
    }
    
    return filtered
        .filter((container) => container && container.id)
        .map((container) => ({
            id: `container-${container.id}`,
            type: "container" as EntityType,
            title: container.containerNumber,
            subtitle: `${container.route.from} → ${container.route.to}`,
            url: `${basePath}/containers/${container.id}`,
            metadata: { container },
        }));
};

// Ricerca shippers
const searchShippers = (searchTerm: string, basePath: string): SearchResult[] => {
    if (!searchTerm) return [];
    
    const accounts = accountService.search({ 
        search: searchTerm,
        userType: "SHIPPER",
    });
    
    return accounts
        .filter((account) => account && account.id)
        .map((account) => ({
            id: `shipper-${account.id}`,
            type: "shipper" as EntityType,
            title: account.legalName,
            subtitle: account.email,
            url: `${basePath}/shippers/${account.id}`,
            metadata: { account },
        }));
};

// Ricerca receivers
const searchReceivers = (searchTerm: string, basePath: string): SearchResult[] => {
    if (!searchTerm) return [];
    
    const accounts = accountService.search({ 
        search: searchTerm,
        userType: "RECEIVER",
    });
    
    return accounts
        .filter((account) => account && account.id)
        .map((account) => ({
            id: `receiver-${account.id}`,
            type: "receiver" as EntityType,
            title: account.legalName,
            subtitle: account.email,
            url: `${basePath}/receivers/${account.id}`,
            metadata: { account },
        }));
};

// Ricerca accounts (solo admin)
const searchAccounts = (searchTerm: string, basePath: string): SearchResult[] => {
    if (!searchTerm) return [];
    
    const accounts = accountService.search({ search: searchTerm });
    
    return accounts
        .filter((account) => account && account.id)
        .map((account) => ({
            id: `account-${account.id}`,
            type: "account" as EntityType,
            title: account.legalName,
            subtitle: `${account.userType} - ${account.email}`,
            url: `${basePath}/accounts/${account.id}/edit`,
            metadata: { account },
        }));
};

// Ricerca address book
const searchAddressBook = (searchTerm: string, basePath: string): SearchResult[] => {
    if (!searchTerm) return [];
    
    const accountId = localStorage.getItem("accountId");
    if (!accountId) return [];
    
    const entries = addressBookService.search({ 
        search: searchTerm,
        accountId,
    });
    
    return entries
        .filter((entry) => entry && entry.id)
        .map((entry) => ({
            id: `address-${entry.id}`,
            type: "address" as EntityType,
            title: `${entry.firstName} ${entry.lastName}`,
            subtitle: `${entry.role} - ${entry.email}`,
            url: `${basePath}/address-book`,
            metadata: { entry },
        }));
};

// Ricerca documenti (raggruppati per container)
const searchDocuments = (searchTerm: string, basePath: string): DocumentGroup[] => {
    if (!searchTerm) return [];
    
    const allDocuments = documentService.getAll();
    const searchLower = searchTerm.toLowerCase();
    
    // Filtra documenti che matchano la ricerca
    const matchingDocs = allDocuments.filter(
        (doc) => doc.name.toLowerCase().includes(searchLower)
    );
    
    // Raggruppa per container
    const grouped = new Map<string, typeof matchingDocs>();
    matchingDocs.forEach((doc) => {
        if (!grouped.has(doc.containerId)) {
            grouped.set(doc.containerId, []);
        }
        grouped.get(doc.containerId)!.push(doc);
    });
    
    // Ottieni info container
    const groups: DocumentGroup[] = [];
    grouped.forEach((docs, containerId) => {
        const container = containerService.getById(containerId);
        if (container) {
            groups.push({
                containerId,
                containerNumber: container.containerNumber,
                containerUrl: `${basePath}/containers/${containerId}`,
                documents: docs
                    .filter((doc) => doc && doc.id)
                    .map((doc) => ({
                        id: `document-${doc.id}`,
                        type: "document" as EntityType,
                        title: doc.name,
                        subtitle: documentService.getDocumentTypeDisplayName(doc.type),
                        url: `${basePath}/containers/${containerId}`, // Naviga al container, non direttamente al documento
                        metadata: { document: doc, container },
                    })),
            });
        }
    });
    
    return groups;
};

// Funzione principale di ricerca
export const performSearch = (
    query: string,
    selectedEntity: EntityType | null
): { results: SearchResult[]; documentGroups: DocumentGroup[] } => {
    const userType = getCurrentUserType();
    const basePath = getBasePath();
    
    if (!query.trim()) {
        return { results: [], documentGroups: [] };
    }
    
    // Se c'è un filtro selezionato, usa quello
    const entity = selectedEntity || null;
    const { searchTerm } = parseShortcut(query);
    
    // Se viene specificato un shortcut nella query, usa quello invece del filtro
    const parsed = parseShortcut(query);
    const effectiveEntity = parsed.entity || entity;
    
    if (effectiveEntity) {
        if (!canSearchEntity(effectiveEntity, userType)) {
            return { results: [], documentGroups: [] };
        }
        
        switch (effectiveEntity) {
            case "container":
                return { results: searchContainers(parsed.searchTerm || searchTerm, userType, basePath), documentGroups: [] };
            case "shipper":
                return { results: searchShippers(parsed.searchTerm || searchTerm, basePath), documentGroups: [] };
            case "receiver":
                return { results: searchReceivers(parsed.searchTerm || searchTerm, basePath), documentGroups: [] };
            case "account":
                return { results: searchAccounts(parsed.searchTerm || searchTerm, basePath), documentGroups: [] };
            case "address":
                return { results: searchAddressBook(parsed.searchTerm || searchTerm, basePath), documentGroups: [] };
            case "document":
                return { results: [], documentGroups: searchDocuments(parsed.searchTerm || searchTerm, basePath) };
        }
    }
    
    // Ricerca senza filtro: cerca in tutte le entità permesse
    const allResults: SearchResult[] = [];
    
    // Per gli admin, cerca solo negli account
    if (userType === "ADMIN") {
        allResults.push(...searchAccounts(searchTerm, basePath));
        return { results: allResults, documentGroups: [] };
    }
    
    // Per altri tipi di utente, cerca nelle entità permesse
    if (canSearchEntity("container", userType)) {
        allResults.push(...searchContainers(searchTerm, userType, basePath));
    }
    if (canSearchEntity("shipper", userType)) {
        allResults.push(...searchShippers(searchTerm, basePath));
    }
    if (canSearchEntity("receiver", userType)) {
        allResults.push(...searchReceivers(searchTerm, basePath));
    }
    if (canSearchEntity("address", userType)) {
        allResults.push(...searchAddressBook(searchTerm, basePath));
    }
    
    // Per documenti, sempre raggruppare
    const documentGroups = canSearchEntity("document", userType) 
        ? searchDocuments(searchTerm, basePath)
        : [];
    
    return { results: allResults, documentGroups };
};

